// src/pages/CardReportingPage.js

import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Container,
  Typography,
  Box,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  Autocomplete,
  TextField,
  Stack,
  Chip,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Button,
  IconButton,
  Tooltip,
  Avatar,
  Switch,
  FormControlLabel
} from '@mui/material'
import DvrIcon from '@mui/icons-material/Dvr';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc
} from 'firebase/firestore'
import { db } from '../firebase'
import { useAuth } from '../contexts/AuthContext'

// Çoklu dil
import { useTranslation } from 'react-i18next'
import { DataGrid, GridToolbar, GridToolbarContainer } from '@mui/x-data-grid';
import * as XLSX from 'xlsx';
import FileDownloadIcon from '@mui/icons-material/FileDownload';


export default function CardReportingPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const { currentUser, organization } = useAuth()

  // --------------------------------------------------------------------------
  // STATE
  // --------------------------------------------------------------------------
  const [selectedChannel, setSelectedChannel] = useState(null)
  const [selectedForm, setSelectedForm] = useState(null)
  const [metaLoading, setMetaLoading] = useState(true)

  const [selectedTags, setSelectedTags] = useState([])

  const [rawRows, setRawRows] = useState([])
  const [loadingAnswers, setLoadingAnswers] = useState(false)

  const [usersMap, setUsersMap] = useState({})

  const [availableQuestions, setAvailableQuestions] = useState([])
  const [selectedQuestions, setSelectedQuestions] = useState([])
  const [questionMetaMap, setQuestionMetaMap] = useState({})
  const [showCardView, setShowCardView] = useState(false)

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  })
  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false })

  // --------------------------------------------------------------------------
  // HELPER FUNCTIONS
  // --------------------------------------------------------------------------
  const stripHtmlTags = (htmlString = '') => {
    const div = document.createElement('div')
    div.innerHTML = htmlString
    return div.textContent || div.innerText || ''
  }

  const isLikelyImageUrl = (str) => {
    if (typeof str !== 'string') return false
    return /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(str)
  }

  const isLikelyVideoUrl = (str) => {
    if (typeof str !== 'string') return false
    return (
      /\.(mp4|webm|mov|avi|mkv)$/i.test(str) ||
      str.includes('firebasestorage.googleapis.com')
    )
  }

  // --------------------------------------------------------------------------
  // URL parametreleri ile otomatik seçim
  // --------------------------------------------------------------------------
  useEffect(() => {
    const formId = searchParams.get('formId')
    if (!formId) {
      setSnackbar({
        open: true,
        message: t('cardReporting.noFormFound'),
        severity: 'error'
      })
      setMetaLoading(false)
      return
    }

    const fetchMeta = async () => {
      try {
        const formSnap = await getDoc(doc(db, 'questionForms', formId))
        if (!formSnap.exists()) {
          setSnackbar({
            open: true,
            message: t('cardReporting.noFormFound'),
            severity: 'error'
          })
          setMetaLoading(false)
          return
        }

        const formData = { id: formSnap.id, ...formSnap.data() }

        if (
          organization?.id &&
          formData.organizationId &&
          formData.organizationId !== organization.id
        ) {
          setSnackbar({
            open: true,
            message: t('cardReporting.noAccess'),
            severity: 'error'
          })
          setMetaLoading(false)
          return
        }

        if (
          organization?.id &&
          !formData.organizationId &&
          formData.createdBy &&
          currentUser?.uid !== formData.createdBy
        ) {
          setSnackbar({
            open: true,
            message: t('cardReporting.noAccess'),
            severity: 'error'
          })
          setMetaLoading(false)
          return
        }

        if (
          !formData.organizationId &&
          organization?.id &&
          formData.createdBy &&
          currentUser?.uid === formData.createdBy
        ) {
          await updateDoc(doc(db, 'questionForms', formData.id), {
            organizationId: organization.id
          })
          formData.organizationId = organization.id
        }

        setSelectedForm(formData)

        // Kanal bilgisi
        let channelObj = null
        const fallbackChannelId = formData.channelId || searchParams.get('channelId') || null
        if (formData.channelId) {
          try {
            const chSnap = await getDoc(doc(db, 'channels', formData.channelId))
            if (chSnap.exists()) {
              channelObj = { id: chSnap.id, ...chSnap.data() }
            }
          } catch (error) {
            console.error('Kanal bilgisi okunamadı:', error)
          }
        }
        if (channelObj || fallbackChannelId) {
          setSelectedChannel(channelObj || { id: fallbackChannelId, name: fallbackChannelId })
        }

        setSelectedTags([])
        setSelectedQuestions([])
      } catch (error) {
        console.error('Form bilgisi okunamadı:', error)
        setSnackbar({
          open: true,
          message: t('cardReporting.noFormFound'),
          severity: 'error'
        })
      } finally {
        setMetaLoading(false)
      }
    }

    fetchMeta()
  }, [searchParams, t, organization, currentUser])

  // --------------------------------------------------------------------------
  // 0) Tüm kullanıcıları çek -> usersMap (userId -> userEmail)
  // --------------------------------------------------------------------------
  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const userSnap = await getDocs(collection(db, 'users'))
        const tempMap = {}
        userSnap.forEach((docSnap) => {
          const data = docSnap.data()
          const fullName = `${data.firstName || ''} ${data.lastName || ''}`.trim()
          tempMap[docSnap.id] = fullName || data.email || docSnap.id
        })
        setUsersMap(tempMap)
      } catch (error) {
        console.error('users alınırken hata:', error)
      }
    }
    fetchAllUsers()
  }, [])

  // --------------------------------------------------------------------------
  // 4) Seçilen formun cevaplarını çek (ÖNEMLİ: card sorting)
  // --------------------------------------------------------------------------
  useEffect(() => {
    const channelId = selectedChannel?.id || selectedForm?.channelId
    if (!selectedForm || !channelId) {
      setRawRows([])
      return
    }

    const fetchAnswers = async () => {
      try {
        setLoadingAnswers(true)
        const respRef = collection(db, 'responses')
        const qResp = query(
          respRef,
          where('channelId', '==', channelId),
          where('questionFormId', '==', selectedForm.id)
        )
        const respSnap = await getDocs(qResp)
        const rawResponses = respSnap.docs.map((docSnap) => ({
          docId: docSnap.id,
          ...docSnap.data()
        }))

        const completedResponses = rawResponses.filter((resp) => resp.status === 'completed')

        // Soruları çekerek kart sıralama için kart ve grup bilgilerini al
        const questionsRef = collection(db, "questions");
        const qQuery = query(
          questionsRef,
          where("questionFormId", "==", selectedForm.id)
        );
        const questionsSnap = await getDocs(qQuery);
        const questionsMap = {};
        questionsSnap.forEach((doc) => {
          questionsMap[doc.id] = doc.data();
        });

        const userTagMap = {}
        ;(selectedChannel?.memberDetails || []).forEach((md) => {
          userTagMap[md.userId] = md.tags || []
        })

        const allRows = []
        completedResponses.forEach((resp) => {
          const { userId, finalAnswers, completedAt } = resp
          const userEmail = usersMap[userId] || userId
          const userTags = userTagMap[userId] || []
          const dateStr = completedAt?.toDate
            ? completedAt.toDate().toLocaleString()
            : ''

          if (!Array.isArray(finalAnswers)) return

          finalAnswers.forEach((fa, idx) => {
            let textAnswer = ''
            let mediaList = []
            let videoUrl = ''
            let multiChoiceValues = null
            let cardSortingGroups = null
            let aiChatTurns = null

            if (typeof fa.answer === 'object' && fa.answer !== null) {
              if ( fa.answer.hasOwnProperty('unsorted') ) {
                const groupLines = []
                const localMedia = []
            
                // Object.entries: Örn "Grup 1" => [ { text, imageUrl }, ... ]
                Object.entries(fa.answer).forEach(([groupName, cardsArray]) => {
                  // Örnek: groupName "unsorted" olduğunda ekrana "unsorted" yazmak istemezseniz,
                  // burada farklı bir koşulla atlayabilirsiniz. (Örneğin if (groupName === 'unsorted' && !cardsArray.length) return ...)
                  if (!Array.isArray(cardsArray)) return
            
                  // Kartların text + imageUrl'lerini işleyelim
                  const cardTexts = cardsArray.map((cardObj) => {
                    let cText = cardObj.text || ''                
                    return cText
                  })
            
                  if (cardsArray.length > 0) {
                    groupLines.push(`${groupName}: ${cardTexts.join(', ')}`)
                  }
                })
            
                // Grupları <br/> ile birleştirip textAnswer’a koyuyoruz
                textAnswer = groupLines.join('<br/>')
            
                // Bulunan tüm resimleri mediaList’e atadık
                mediaList = localMedia
                cardSortingGroups = groupLines

              } else if (fa.answer.media && Array.isArray(fa.answer.media)) {
                // openEnded tipi: hem text hem media
                if (fa.answer.text) textAnswer = fa.answer.text
                mediaList = fa.answer.media.map((mObj) => ({
                  type: mObj.fileType || 'image',
                  url: mObj.remoteUrl
                }))
              } else if (fa.answer.videoUrl) {
                // videoQuestion tipi
                videoUrl = fa.answer.videoUrl
              } else if (fa.answer.collageSingleUrl) {       
                // photoCollage
                mediaList = [{ type: 'image', url: fa.answer.collageSingleUrl }]
              } else if (Array.isArray(fa.answer)) {      
                // AI-Moderator
                if (
                  fa.answer.length > 0 &&
                  fa.answer.every(
                    (msgObj) =>
                      msgObj &&
                      typeof msgObj === 'object' &&
                      'role' in msgObj &&
                      'content' in msgObj
                  )
                ) {
                  // Chat benzeri format -> role + content
                  aiChatTurns = fa.answer.map((msgObj) => ({
                    role: msgObj.role || '',
                    content: msgObj.content || ''
                  }))
                  // Excel export ve diğer yerler için düz metin de oluşturalım
                  textAnswer = aiChatTurns
                    .map((msgObj) => `[${msgObj.role}] ${msgObj.content}`)
                    .join('<br/>')
                } else {
                  // Çoklu string array (multipleChoice)
                  textAnswer = fa.answer.join(', ')
                  multiChoiceValues = fa.answer
                }
              } else if (typeof fa.answer.text === 'string') {
                // openEnded sadece text
                textAnswer = fa.answer.text
              }               
            } else if (Array.isArray(fa.answer)) {   
              // multipleChoice ya da AI as array of strings
              textAnswer = fa.answer.join(', ')
              multiChoiceValues = fa.answer
            } else if (typeof fa.answer === 'string') {
              // singleChoice, vs
              textAnswer = fa.answer
            } else if (typeof fa.answer === 'number') {
              // rating
              textAnswer = String(fa.answer)
            } 

            allRows.push({
              id: resp.docId + '_' + idx,
              docId: resp.docId,
              userId: userId,
              userEmail,
              userTags,
              questionText: fa.questionText,
              textAnswer,
              mediaList,
              videoUrl,
              multiChoiceValues,
              cardSortingGroups,
              aiChatTurns,
              createdAt: dateStr
            })
          })
        })

        setRawRows(allRows)
        setLoadingAnswers(false)
      } catch (error) {
        console.error('Cevaplar alınırken hata:', error)
        setSnackbar({
          open: true,
          message: t('cardReporting.answersLoadingError'),
          severity: 'error'
        })
        setLoadingAnswers(false)
      }
    }

    fetchAnswers()
  }, [selectedForm, selectedChannel, usersMap, t])

  // --------------------------------------------------------------------------
  // Filtreler ve gruplama
  // --------------------------------------------------------------------------
  const filteredRowsByTags = useMemo(() => {
    let filtered = rawRows

    if (selectedTags.length > 0) {
      filtered = filtered.filter((r) => {
        return selectedTags.some((tag) => r.userTags.includes(tag))
      })
    }

    // Sıralamayı, availableQuestions sırasına göre yap
    filtered = filtered.sort((a, b) => {
      const aIndex = availableQuestions.indexOf(a.questionText)
      const bIndex = availableQuestions.indexOf(b.questionText)
      return aIndex - bIndex
    })

    return filtered
  }, [rawRows, selectedTags, availableQuestions])

  const filteredRowsForCards = useMemo(() => {
    if (!selectedQuestions.length) return filteredRowsByTags
    return filteredRowsByTags.filter((r) => selectedQuestions.includes(r.questionText))
  }, [filteredRowsByTags, selectedQuestions])

  const buildGroupedUsers = useCallback(
    (rows) => {
      const userMap = {}
      rows.forEach((r) => {
        const userKey = r.userEmail
        if (!userMap[userKey]) {
          userMap[userKey] = {
            userEmail: r.userEmail,
            userTags: r.userTags,
            responseDocId: r.docId,
            answers: [],
            createdAtList: []
          }
        }
        userMap[userKey].answers.push({
          questionText: r.questionText,
          textAnswer: r.textAnswer,
          mediaList: r.mediaList,
          videoUrl: r.videoUrl,
          multiChoiceValues: r.multiChoiceValues,
          cardSortingGroups: r.cardSortingGroups,
          aiChatTurns: r.aiChatTurns
        })
        userMap[userKey].createdAtList.push(r.createdAt)
      })

      return Object.values(userMap).map((obj) => {
        const earliestDate = obj.createdAtList[0] || ''
        return {
          ...obj,
          answers: obj.answers.sort((a, b) => {
            const aIndex = availableQuestions.indexOf(a.questionText)
            const bIndex = availableQuestions.indexOf(b.questionText)
            return aIndex - bIndex
          }),
          displayDate: earliestDate
        }
      })
    },
    [availableQuestions]
  )

  const tableUserGroups = useMemo(
    () => buildGroupedUsers(filteredRowsByTags),
    [buildGroupedUsers, filteredRowsByTags]
  )

  const cardUserGroups = useMemo(
    () => buildGroupedUsers(filteredRowsForCards),
    [buildGroupedUsers, filteredRowsForCards]
  )

  // --------------------------------------------------------------------------
  // "Reddet" Butonu (Örnek: Yanıtı tekrar 'inProgress'e çekmek vb.)
  // --------------------------------------------------------------------------
  const handleReject = async (responseDocId) => {
    if (!responseDocId) return
    try {
      await updateDoc(doc(db, 'responses', responseDocId), {
        status: 'inProgress',
        progress: 0,
        rejected: true // sadece bir flag
      })

      setRawRows((prev) => prev.filter((row) => row.docId !== responseDocId))

      setSnackbar({
        open: true,
        message: t('cardReporting.rejectSuccess'),
        severity: 'success'
      })
    } catch (err) {
      console.error('Reddetme işleminde hata:', err)
      setSnackbar({
        open: true,
        message: t('cardReporting.rejectError'),
        severity: 'error'
      })
    }
  }

  // --------------------------------------------------------------------------
  // --------------------------------------------------------------------------
  // File download yardımı
  // --------------------------------------------------------------------------
  const sanitizeFileName = (name) => {
    return name.replace(/[^a-z0-9]/gi, '_')
  }

  const getFileExtension = (url, defaultType) => {
    const match = url.match(/\.([0-9a-z]+)(?:[?#]|$)/i)
    if (match && match[1]) {
      return match[1]
    }
    if (defaultType === 'image') return 'jpg'
    if (defaultType === 'video') return 'mp4'
    return ''
  }

  async function downloadWithCustomName(url, fileName) {
    try {
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      const blob = await response.blob()
      const objUrl = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = objUrl
      link.download = fileName
      document.body.appendChild(link)
      link.click()

      document.body.removeChild(link)
      URL.revokeObjectURL(objUrl)
    } catch (error) {
      console.error('Dosya indirilemedi:', error)
    }
  }

  // --------------------------------------------------------------------------
  // Excel'e Aktar
  // --------------------------------------------------------------------------
  const formatAnswerForExport = (answer) => {
    if (!answer) return ''

    const parts = []
    const plainText = answer.textAnswer ? stripHtmlTags(answer.textAnswer) : ''

    if (Array.isArray(answer.aiChatTurns) && answer.aiChatTurns.length > 0) {
      parts.push(
        answer.aiChatTurns
          .map((turn) => {
            const role = (turn.role || '').toUpperCase()
            const content = stripHtmlTags(turn.content || '')
            return `[${role}] ${content}`
          })
          .join('\n')
      )
    } else if (
      Array.isArray(answer.cardSortingGroups) &&
      answer.cardSortingGroups.length > 1
    ) {
      parts.push(
        answer.cardSortingGroups
          .map((grp) => stripHtmlTags(typeof grp === 'string' ? grp : ''))
          .join('\n')
      )
    } else if (Array.isArray(answer.multiChoiceValues) && answer.multiChoiceValues.length > 0) {
      parts.push(
        answer.multiChoiceValues
          .map((val) => stripHtmlTags(typeof val === 'string' ? val : ''))
          .join(', ')
      )
    } else if (plainText) {
      parts.push(plainText)
    }

    if (Array.isArray(answer.mediaList) && answer.mediaList.length > 0) {
      parts.push(
        answer.mediaList
          .filter((m) => m?.url)
          .map((m) => `${m.type === 'video' ? 'Video' : 'Image'}: ${m.url}`)
          .join('\n')
      )
    }

    if (answer.videoUrl) {
      parts.push(`Video: ${answer.videoUrl}`)
    }

    return parts.filter(Boolean).join('\n')
  }

  const handleExportToExcel = () => {
    if (tableUserGroups.length === 0) {
      setSnackbar({
        open: true,
        message: t('cardReporting.noDataToExport'),
        severity: 'warning'
      });
      return;
    }

    const headers = [
      t('cardReporting.userEmail'),
      t('cardReporting.date'),
      ...questionListForColumns.map((qText) => stripHtmlTags(qText))
    ]

    const excelData = tableUserGroups.map((user) => {
      const row = {
        [t('cardReporting.userEmail')]: user.userEmail,
        [t('cardReporting.date')]: user.displayDate
      }

      questionListForColumns.forEach((qText) => {
        const matchedAnswer = user.answers.find((a) => a.questionText === qText)
        row[stripHtmlTags(qText)] = formatAnswerForExport(matchedAnswer)
      })

      return row
    })

    const worksheet = XLSX.utils.json_to_sheet(excelData, { header: headers });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Responses");
    XLSX.writeFile(workbook, "UserResponses.xlsx");
  };

  // Tabloyu Typeform benzeri yapmak için:
  // - Kullanıcı başına tek satır
  // - Sütun başlıkları soru metni
  // - Hücre içinde cevap ve varsa medya önizleme + link
  const questionListForColumns = useMemo(() => availableQuestions, [availableQuestions])

  const renderAnswerCell = useCallback((params) => {
    const ans = params.value
    if (!ans) {
      return (
        <Typography variant="body2" color="text.secondary">
          -
        </Typography>
      )
    }

    const { textAnswer, mediaList = [], videoUrl, multiChoiceValues, cardSortingGroups, aiChatTurns } = ans
    const textIsImage = isLikelyImageUrl(textAnswer)
    const textIsVideo = isLikelyVideoUrl(textAnswer)
    const normalizedMediaList = Array.isArray(mediaList) ? mediaList : []

    // AI-Moderator chat answers -> separate chips per turn with icons
    if (
      Array.isArray(aiChatTurns) &&
      aiChatTurns.length > 0 &&
      !textIsImage &&
      !textIsVideo &&
      normalizedMediaList.length === 0 &&
      !videoUrl
    ) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 0.4,
            py: 0.3
          }}
        >
          {aiChatTurns.map((turn, idx) => {
            const role = (turn.role || '').toLowerCase()
            const content = stripHtmlTags(turn.content || '')
            if (!content) return null
            let icon = null
            let bgColor = '#f3f4ff'
            let title = ''

            if (role === 'assistant') {
              icon = <SmartToyIcon sx={{ fontSize: 14 }} />
              bgColor = '#e0f2fe'
              title = 'AI'
            } else if (role === 'user') {
              icon = <PersonIcon sx={{ fontSize: 14 }} />
              bgColor = '#e5e7eb'
              title = 'User'
            } else if (role === 'question' || role === 'system') {
              icon = <HelpOutlineIcon sx={{ fontSize: 14 }} />
              bgColor = '#ede9fe'
              title = 'Question'
            }

            return (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  px: 1.2,
                  py: 0.35,
                  borderRadius: 999,
                  backgroundColor: bgColor,
                  fontSize: 13,
                  maxWidth: '100%',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  gap: 0.5
                }}
                title={title}
              >
                {icon}
                <span>{content}</span>
              </Box>
            )
          })}
        </Box>
      )
    }

    // Card sorting answers with multiple groups -> show each group as a separate pill
    if (
      Array.isArray(cardSortingGroups) &&
      cardSortingGroups.length > 1 &&
      !textIsImage &&
      !textIsVideo &&
      normalizedMediaList.length === 0 &&
      !videoUrl
    ) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 0.5,
            py: 0.3
          }}
        >
          {cardSortingGroups.map((groupLine, idx) => (
            <Box
              key={idx}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 1.2,
                py: 0.4,
                borderRadius: 999,
                backgroundColor: '#eef2ff',
                fontSize: 13,
                maxWidth: '100%',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
              title={typeof groupLine === 'string' ? stripHtmlTags(groupLine) : ''}
            >
              {typeof groupLine === 'string' ? stripHtmlTags(groupLine) : ''}
            </Box>
          ))}
        </Box>
      )
    }

    // Multiple choice answers -> multiple compact pills
    if (
      Array.isArray(multiChoiceValues) &&
      multiChoiceValues.length > 0 &&
      !textIsImage &&
      !textIsVideo &&
      normalizedMediaList.length === 0 &&
      !videoUrl
    ) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            py: 0.3
          }}
        >
          {multiChoiceValues.map((choice, idx) => (
            <Box
              key={idx}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 1.4,
                py: 0.4,
                borderRadius: 999,
                backgroundColor: '#f3f4ff',
                fontSize: 13,
                maxWidth: '100%',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
              title={typeof choice === 'string' ? stripHtmlTags(choice) : ''}
            >
              {typeof choice === 'string' ? stripHtmlTags(choice) : ''}
            </Box>
          ))}
        </Box>
      )
    }

    // Simple text-only answers -> compact pill like Typeform
    if (
      textAnswer &&
      !textIsImage &&
      !textIsVideo &&
      normalizedMediaList.length === 0 &&
      !videoUrl
    ) {
      const plain = stripHtmlTags(textAnswer)
      return (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            px: 1.4,
            py: 0.4,
            borderRadius: 999,
            backgroundColor: '#f3f4ff',
            fontSize: 13,
            maxWidth: '100%',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {plain}
        </Box>
      )
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, py: 0.5 }}>
        {textAnswer && !textIsImage && !textIsVideo && (
          <Box
            sx={{ fontSize: 14, lineHeight: 1.4 }}
            dangerouslySetInnerHTML={{ __html: textAnswer }}
          />
        )}

        {textAnswer && textIsImage && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CardMedia
              component="img"
              src={textAnswer}
              alt={t('cardReporting.answerImage')}
              sx={{ width: 120, height: 'auto', borderRadius: 1 }}
            />
            <Tooltip title={t('cardReporting.viewMedia', { defaultValue: 'Görüntüle' })}>
              <IconButton
                size="small"
                component="a"
                href={textAnswer}
                target="_blank"
                rel="noopener noreferrer"
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {textAnswer && textIsVideo && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <video width="140" height="auto" controls style={{ borderRadius: 8 }}>
              <source src={textAnswer} type="video/mp4" />
              {t('cardReporting.unsupportedMedia')}
            </video>
            <Tooltip title={t('cardReporting.viewMedia', { defaultValue: 'Görüntüle' })}>
              <IconButton
                size="small"
                component="a"
                href={textAnswer}
                target="_blank"
                rel="noopener noreferrer"
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {normalizedMediaList.length > 0 &&
          normalizedMediaList.map((mItem, idx) => {
            if (!mItem?.url) return null

            if (mItem.type === 'video') {
              return (
                <Box
                  key={idx}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  <video width="140" height="auto" controls style={{ borderRadius: 8 }}>
                    <source src={mItem.url} type="video/mp4" />
                    {t('cardReporting.unsupportedMedia')}
                  </video>
                  <Tooltip title={t('cardReporting.viewMedia', { defaultValue: 'Görüntüle' })}>
                    <IconButton
                      size="small"
                      component="a"
                      href={mItem.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )
            }

            return (
              <Box
                key={idx}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                <CardMedia
                  component="img"
                  src={mItem.url}
                  alt={t('cardReporting.answerImage')}
                  sx={{ width: 120, height: 'auto', borderRadius: 1 }}
                />
                <Tooltip title={t('cardReporting.viewMedia', { defaultValue: 'Görüntüle' })}>
                  <IconButton
                    size="small"
                    component="a"
                    href={mItem.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )
          })}

        {videoUrl && typeof videoUrl === 'string' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <video width="140" height="auto" controls style={{ borderRadius: 8 }}>
              <source src={videoUrl} type="video/mp4" />
              {t('cardReporting.unsupportedMedia')}
            </video>
            <Button
              size="small"
              component="a"
              href={videoUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('cardReporting.viewMedia', { defaultValue: 'Link' })}
            </Button>
          </Box>
        )}
      </Box>
    )
  }, [t])

  const columns = useMemo(() => {
    const baseColumns = [
      {
        field: 'userEmail',
        headerName: t('cardReporting.userEmail'),
        flex: 1.1,
        minWidth: 160
      },
      {
        field: 'createdAt',
        headerName: t('cardReporting.date'),
        flex: 1,
        minWidth: 150
      }
    ]

    const questionColumns = questionListForColumns.map((qText, idx) => {
      const meta = questionMetaMap[qText] || {}
      // Soru metnini sadeleştir
      const headerLabel = stripHtmlTags(qText || '')
      // Soru tipi etiketini al, yoksa boş bırak
      const typeKey = meta.type || ''
      const typeLabel = typeKey
        ? t(`questionTypes.${typeKey}`, { defaultValue: typeKey })
        : ''

      return {
        field: `question_${idx}`,
        headerName: headerLabel,
        flex: 1.6,
        minWidth: 220,
        sortable: false,
        renderCell: renderAnswerCell,
        renderHeader: () => (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 0.5,
              py: 0.25
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, lineHeight: 1.3 }}
            >
              {headerLabel}
            </Typography>
            {typeLabel && (
              <Chip
                label={typeLabel}
                size="small"
                sx={{
                  height: 18,
                  fontSize: 10,
                  borderRadius: 999,
                  backgroundColor: '#e0ebff',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.4
                }}
              />
            )}
          </Box>
        )
      }
    })

    return [...baseColumns, ...questionColumns]
  }, [questionListForColumns, renderAnswerCell, t, questionMetaMap])

  const dataGridRows = useMemo(() => {
    return tableUserGroups.map((user, idx) => {
      const row = {
        id: user.responseDocId || user.userEmail || idx,
        userEmail: user.userEmail,
        userTags: user.userTags,
        createdAt: user.displayDate
      }

      questionListForColumns.forEach((qText, qIdx) => {
        const matchedAnswer = user.answers.find((a) => a.questionText === qText)
        row[`question_${qIdx}`] = matchedAnswer || null
      })

      return row
    })
  }, [tableUserGroups, questionListForColumns])

  // DataGrid Custom Toolbar
  const CustomToolbar = () => (
    <GridToolbarContainer>
      <GridToolbar />
      <Button 
        startIcon={<FileDownloadIcon />}
        onClick={handleExportToExcel}
        sx={{ ml: 2 }}
      >
        {t('cardReporting.exportExcel')}
      </Button>
    </GridToolbarContainer>
  );

  // 5) Seçilen form için soruları çek (Soru ismi filtresi için)
  useEffect(() => {
    if (!selectedForm) {
      setAvailableQuestions([])
      setQuestionMetaMap({})
      return
    }
  
    const fetchQuestions = async () => {
      try {
        const q = query(
          collection(db, 'questions'),
          where('questionFormId', '==', selectedForm.id)
        )
        const querySnapshot = await getDocs(q)
        
        // Soruları sortOrder'a göre sırala
        const questions = querySnapshot.docs
          .map(doc => ({
            ...doc.data(),
            id: doc.id
          }))
          .sort((a, b) => a.sortOrder - b.sortOrder)
  
        // Tekilleştirme yapmadan doğrudan sıralı listeyi al
        const questionTexts = questions.map(q => q.text)
        setAvailableQuestions(questionTexts)
  
        // Soru metnine göre meta (özellikle soru tipi) haritasını oluştur
        const metaMap = {}
        questions.forEach((q) => {
          const qText = q.text
          if (!qText) return
          metaMap[qText] = {
            // Projede kullanılan alan ismine göre önce q.type, yoksa questionType/kind fallback
            type: q.type || q.questionType || q.kind || ''
          }
        })
        setQuestionMetaMap(metaMap)
      } catch (error) {
        console.error('Sorular alınırken hata:', error)
      }
    }
  
    fetchQuestions()
  }, [selectedForm])

  const navigationQuery = useMemo(() => {
    const params = new URLSearchParams()
    if (selectedForm?.id) params.set('formId', selectedForm.id)
    const channelId = selectedChannel?.id || selectedForm?.channelId
    if (channelId) params.set('channelId', channelId)
    if (selectedForm?.workspaceId) params.set('workspaceId', selectedForm.workspaceId)
    const qs = params.toString()
    return qs ? `?${qs}` : ''
  }, [selectedForm, selectedChannel])

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------
  return (
    <Container
      maxWidth={false}
      sx={{
        minHeight: '100vh',
        py: { xs: 3, sm: 4 },
        px: { xs: 2, sm: 3, md: 4 },
        background: 'linear-gradient(180deg, #f7f9fc 0%, #eef3fb 100%)'
      }}
    >
      <Box
        sx={{
          width: '100%',
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <Paper
          sx={{
            p: { xs: 2.5, sm: 3 },
            borderRadius: 3,
            background:
              'linear-gradient(135deg, rgba(63, 81, 181, 0.06) 0%, rgba(33, 150, 243, 0.06) 100%)',
            border: '1px solid rgba(63, 81, 181, 0.08)',
            boxShadow: '0 10px 30px rgba(63, 81, 181, 0.12)'
          }}
        >

          {/* HEADER ROW: title solda, tab bar sağda */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
            spacing={2}
          >
            
            {/* SOL BLOK: Title, subtitle + published */}
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <DvrIcon color="primary" />
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {t('cardReporting.pageTitle')}
                </Typography>
              </Stack>

              {/* Subtitle + published aynı satırda */}
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  {selectedForm?.name || t('cardReporting.pageTitle')}
                </Typography>

                {selectedForm?.status && (
                  <Chip
                    label={selectedForm.status}
                    color="primary"
                    size="small"
                    variant="outlined"
                    sx={{
                      height: 22,
                      fontSize: '0.7rem',
                      borderRadius: 1.5,
                      fontWeight: 600,
                      px: 0.6
                    }}
                  />
                )}
              </Stack>
            </Stack>

            {/* TAB BAR */}
            <Stack
              direction={{ xs: 'row', sm: 'row' }}
              spacing={1}
              sx={{
                p: 0.6,
                borderRadius: 999,
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                justifyContent: { xs: 'flex-end', sm: 'flex-start' },
                maxWidth: { xs: '100%', sm: 'auto' }
              }}
            >
              <Button
                component={Link}
                to={`/view-answers${navigationQuery}`}
                variant="outlined"
                size="small"
                sx={{
                  borderRadius: 999,
                  textTransform: 'none',
                  px: { xs: 1.3, sm: 2 },
                  minWidth: { xs: 'auto', sm: 0 },
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }
                }}
              >
                {t('formNavigation.dashboard')}
              </Button>

              <Button
                component={Link}
                to={`/view-answers-card${navigationQuery}`}
                variant="contained"
                size="small"
                sx={{
                  borderRadius: 999,
                  textTransform: 'none',
                  px: { xs: 1.3, sm: 2 },
                  minWidth: { xs: 'auto', sm: 0 },
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  boxShadow: 'none'
                }}
              >
                {t('formNavigation.reporting')}
              </Button>

              <Button
                component={Link}
                to={`/CardAnalyticsPage${navigationQuery}`}
                variant="outlined"
                size="small"
                sx={{
                  borderRadius: 999,
                  textTransform: 'none',
                  px: { xs: 1.3, sm: 2 },
                  minWidth: { xs: 'auto', sm: 0 },
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }
                }}
              >
                {t('formNavigation.analytics')}
              </Button>
            </Stack>

          </Stack>
        </Paper>

        {metaLoading && (
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              display: 'flex',
              justifyContent: 'center',
              borderRadius: 3,
              backgroundColor: '#fff'
            }}
          >
            <CircularProgress />
          </Paper>
        )}

        {!metaLoading && !selectedForm && (
          <Alert severity="warning" sx={{ width: '100%', borderRadius: 3 }}>
            {t('cardReporting.noFormFound')}
          </Alert>
        )}

        {!metaLoading && selectedForm && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center'
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={showCardView}
                    onChange={(e) => setShowCardView(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  showCardView
                    ? t('cardReporting.viewModeCards') 
                    : t('cardReporting.viewModeTable') 
                }
              />
            </Box>

            {!showCardView && (
              <Paper
                sx={{
                  backgroundColor: '#fff',
                  borderRadius: 2,
                  boxShadow: '0 2px 6px rgba(15,23,42,0.04)',
                  border: '1px solid #e5e7eb',
                  pt: 2,
                  pr: 2,
                  pl: 2,
                }}
              >
                <DataGrid
                  rows={dataGridRows}
                  columns={columns}
                  slots={{
                    toolbar: CustomToolbar
                  }}
                  getRowHeight={() => 'auto'}
                  pageSizeOptions={[10, 25, 50]}
                  initialState={{
                    pagination: {
                      paginationModel: { page: 0, pageSize: 10 }
                    }
                  }}
                  disableRowSelectionOnClick
                  disableColumnMenu
                  disableColumnSelector
                  disableDensitySelector
                  disableColumnFilter
                  disableColumnReorder
                  sx={{
                    border: 'none',
                    fontSize: 13,
                    '& .MuiDataGrid-virtualScroller': {
                      backgroundColor: '#fff'
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: '#f9fafb',
                      borderBottom: '1px solid #e5e7eb',
                      fontSize: 13,
                      fontWeight: 600,
                      paddingLeft: 3,
                    },
                    '& .MuiDataGrid-columnSeparator': {
                      display: 'none'
                    },
                    '& .MuiDataGrid-cell': {
                      borderBottom: '1px solid #f3f4f6',
                      paddingTop: 1,
                      paddingBottom: 1,
                      paddingLeft: 3,
                    },
                    '& .MuiDataGrid-row:hover': {
                      backgroundColor: '#f9fafb'
                    },
                    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                      outline: 'none'
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: '1px solid #e5e7eb',
                      backgroundColor: '#fff'
                    }
                  }}
                />
              </Paper>
            )}

            {showCardView && (
              <>
                {availableQuestions.length > 0 && (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 2, sm: 2.5 },
                      backgroundColor: '#fff',
                      borderRadius: 3,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
                      border: '1px solid #edf1f7'
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                      {t('cardReporting.filterByQuestions')}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 1 }}
                    >
                      {t('cardReporting.questionFilterInfo') }
                    </Typography>
                    <Autocomplete
                      multiple
                      options={availableQuestions}
                      value={selectedQuestions}
                      onChange={(event, newValue) => {
                        const sorted = newValue
                          .slice()
                          .sort(
                            (a, b) =>
                              availableQuestions.indexOf(a) - availableQuestions.indexOf(b)
                          )
                        setSelectedQuestions(sorted)
                      }}
                      getOptionLabel={(option) => stripHtmlTags(option)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('cardReporting.selectQuestions')}
                          placeholder={t('cardReporting.selectQuestions')}
                          size="small"
                        />
                      )}
                    />
                  </Paper>
                )}

                <Paper
                  variant="outlined"
                  sx={{
                    p: { xs: 2.5, sm: 3 },
                    backgroundColor: '#f3f4f6',
                    borderRadius: 3,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.04)',
                    border: '1px solid #e5e7eb'
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    {t('cardReporting.userAnswersTitle')}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {t('cardReporting.userAnswersSubtitle')}
                  </Typography>

                  {loadingAnswers ? (
                    <Box textAlign="center">
                      <CircularProgress />
                    </Box>
                  ) : (
                    <Grid container spacing={2}>
                      {cardUserGroups.length === 0 ? (
                        <Grid item xs={12}>
                          <Typography>{t('cardReporting.noAnswersFound')}</Typography>
                        </Grid>
                      ) : (
                        cardUserGroups.map((userObj, index) => {
                          const initials =
                            (userObj.userEmail || '?')
                              .split('@')[0]
                              .split(/[.\s_-]/)
                              .filter(Boolean)
                              .map((p) => p[0]?.toUpperCase())
                              .join('')
                              .slice(0, 2) || '?'

                          return (
                            <Grid item xs={12} key={index}>
                              <Card
                                sx={{
                                  position: 'relative',
                                  borderRadius: 3,
                                  overflow: 'hidden',
                                  backgroundColor: '#ffffff',
                                  border: '1px solid #e5e7eb',
                                  boxShadow: '0 10px 30px rgba(15,23,42,0.06)',
                                  transition: 'all 0.25s ease',
                                  '&:hover': {
                                    boxShadow: '0 18px 45px rgba(15,23,42,0.12)',
                                    transform: 'translateY(-2px)'
                                  }
                                }}
                              >
                                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                                  {/* HEADER: avatar + user info + date + reject button */}
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      justifyContent: 'space-between',
                                      mb: 1.5
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                      <Avatar
                                        sx={{
                                          width: 40,
                                          height: 40,
                                          bgcolor: '#3b82f6',
                                          fontWeight: 700,
                                          fontSize: 16
                                        }}
                                      >
                                        {initials}
                                      </Avatar>
                                      <Box>
                                        <Typography
                                          variant="subtitle2"
                                          sx={{ fontWeight: 700, lineHeight: 1.2 }}
                                        >
                                          {userObj.userEmail}
                                        </Typography>
                                        {userObj.userTags?.length > 0 && (
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              flexWrap: 'wrap',
                                              gap: 0.5,
                                              mt: 0.5
                                            }}
                                          >
                                            {userObj.userTags.map((tag, tagIdx) => (
                                              <Chip
                                                key={tagIdx}
                                                label={tag}
                                                size="small"
                                                sx={{
                                                  height: 20,
                                                  fontSize: 11,
                                                  borderRadius: 999,
                                                  backgroundColor: '#eff6ff',
                                                  borderColor: 'transparent'
                                                }}
                                              />
                                            ))}
                                          </Box>
                                        )}
                                      </Box>
                                    </Box>

                                    <Box
                                      sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-end',
                                        gap: 1
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ whiteSpace: 'nowrap' }}
                                      >
                                        {t('cardReporting.answerDate')}: {userObj.displayDate}
                                      </Typography>
                                      <Button
                                        size="small"
                                        color="error"
                                        variant="outlined"
                                        onClick={() => handleReject(userObj.responseDocId)}
                                        sx={{
                                          textTransform: 'none',
                                          borderRadius: 999,
                                          fontSize: 12,
                                          px: 1.6,
                                          py: 0.3
                                        }}
                                      >
                                        {t('cardReporting.rejectButton')}
                                      </Button>
                                    </Box>
                                  </Box>

                                  {/* CONTENT: answers feed */}
                                  <Box
                                    sx={{
                                      mt: 1,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: 1.5
                                    }}
                                  >
                                    {userObj.answers.map((ans, ansIndex) => {
                                      const questionPlainText = stripHtmlTags(ans.questionText || '')
                                      const textIsImage = isLikelyImageUrl(ans.textAnswer)
                                      const textIsVideo = isLikelyVideoUrl(ans.textAnswer)

                                      return (
                                        <Box
                                          key={ansIndex}
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 1.5
                                          }}
                                        >
                                          {/* mini left indicator line like social thread */}
                                          <Box
                                            sx={{
                                              width: 3,
                                              borderRadius: 999,
                                              background:
                                                'linear-gradient(180deg, #60a5fa 0%, #a855f7 100%)',
                                              alignSelf: 'stretch',
                                              mt: 0.3
                                            }}
                                          />
                                          <Box
                                            sx={{
                                              flex: 1,
                                              p: 1.2,
                                              borderRadius: 2,
                                              backgroundColor: '#f9fafb',
                                              border: '1px solid #e5e7eb'
                                            }}
                                          >
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}
                                            >
                                              {t('cardReporting.question')}: {questionPlainText}
                                            </Typography>

                                            {/* AI‑Moderator structured chat rendering */}
                                            {Array.isArray(ans.aiChatTurns) &&
                                              ans.aiChatTurns.length > 0 && (
                                                <Box sx={{ mt: 0.75 }}>
                                                  <Box
                                                    sx={{
                                                      display: 'flex',
                                                      flexDirection: 'column',
                                                      gap: 0.6
                                                    }}
                                                  >
                                                    {ans.aiChatTurns.map((turn, idx) => {
                                                      const role = (turn.role || '').toLowerCase()
                                                      const content = stripHtmlTags(
                                                        turn.content || ''
                                                      )
                                                      if (!content) return null

                                                      let icon = null
                                                      let bgColor = '#eff6ff'
                                                      let alignSelf = 'flex-start'

                                                      if (role === 'assistant') {
                                                        icon = <SmartToyIcon sx={{ fontSize: 14 }} />
                                                        bgColor = '#e0f2fe'
                                                        alignSelf = 'flex-end'
                                                      } else if (role === 'user') {
                                                        icon = <PersonIcon sx={{ fontSize: 14 }} />
                                                        bgColor = '#e5e7eb'
                                                        alignSelf = 'flex-start'
                                                      } else if (
                                                        role === 'question' ||
                                                        role === 'system'
                                                      ) {
                                                        icon = <HelpOutlineIcon sx={{ fontSize: 14 }} />
                                                        bgColor = '#ede9fe'
                                                        alignSelf = 'center'
                                                      }

                                                      return (
                                                        <Box
                                                          key={idx}
                                                          sx={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            px: 1.2,
                                                            py: 0.5,
                                                            borderRadius: 999,
                                                            backgroundColor: bgColor,
                                                            fontSize: 13,
                                                            maxWidth: '100%',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            gap: 0.6,
                                                            alignSelf
                                                          }}
                                                        >
                                                          {icon}
                                                          <span>{content}</span>
                                                        </Box>
                                                      )
                                                    })}
                                                  </Box>
                                                </Box>
                                              )}

                                            {/* Metin (normal veya cardSortingGroups) - AI chat yoksa göster */}
                                            {(!Array.isArray(ans.aiChatTurns) ||
                                              ans.aiChatTurns.length === 0) &&
                                              (Array.isArray(ans.cardSortingGroups) &&
                                              ans.cardSortingGroups.length > 1 ? (
                                                <Box sx={{ mt: 0.75 }}>
                                                  <Box
                                                    sx={{
                                                      display: 'flex',
                                                      flexDirection: 'column',
                                                      gap: 0.5
                                                    }}
                                                  >
                                                    {ans.cardSortingGroups.map(
                                                      (groupLine, gIdx) => (
                                                        <Box
                                                          key={gIdx}
                                                          sx={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            px: 1.2,
                                                            py: 0.4,
                                                            borderRadius: 999,
                                                            backgroundColor: '#eef2ff',
                                                            fontSize: 13,
                                                            maxWidth: '100%',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis'
                                                          }}
                                                        >
                                                          {typeof groupLine === 'string'
                                                            ? stripHtmlTags(groupLine)
                                                            : ''}
                                                        </Box>
                                                      )
                                                    )}
                                                  </Box>
                                                </Box>
                                              ) : (
                                                ans.textAnswer &&
                                                !textIsImage &&
                                                !textIsVideo && (
                                                  <Box sx={{ mt: 0.75 }}>
                                                    <Box
                                                      sx={{
                                                        mt: 0.2,
                                                        fontSize: 14,
                                                        lineHeight: 1.5
                                                      }}
                                                      dangerouslySetInnerHTML={{
                                                        __html: ans.textAnswer
                                                      }}
                                                    />
                                                  </Box>
                                                )
                                              ))}

                                            {/* Metin (resim linki) */}
                                            {ans.textAnswer &&
                                              textIsImage &&
                                              typeof ans.textAnswer === 'string' && (
                                                <Box sx={{ mt: 0.75 }}>
                                                  <CardMedia
                                                    component="img"
                                                    src={ans.textAnswer}
                                                    alt="Cevap Resim"
                                                    sx={{
                                                      maxWidth: 360,
                                                      borderRadius: 2,
                                                      mt: 0.5,
                                                      boxShadow:
                                                        '0 10px 30px rgba(15,23,42,0.25)'
                                                    }}
                                                  />
                                                </Box>
                                              )}

                                            {/* Metin (video linki) */}
                                            {ans.textAnswer &&
                                              textIsVideo &&
                                              typeof ans.textAnswer === 'string' && (
                                                <Box sx={{ mt: 0.75 }}>
                                                  <video
                                                    width="360"
                                                    height="auto"
                                                    controls
                                                    style={{
                                                      borderRadius: 12,
                                                      marginTop: 4,
                                                      boxShadow:
                                                        '0 10px 30px rgba(15,23,42,0.25)'
                                                    }}
                                                  >
                                                    <source
                                                      src={ans.textAnswer}
                                                      type="video/mp4"
                                                    />
                                                    {t('cardReporting.unsupportedMedia')}
                                                  </video>
                                                </Box>
                                              )}

                                            {/* mediaList => [{ type: 'image'|'video', url: string }] */}
                                            {ans.mediaList && ans.mediaList.length > 0 && (
                                              <Box
                                                sx={{
                                                  display: 'flex',
                                                  gap: 1,
                                                  flexWrap: 'wrap',
                                                  mt: 0.75
                                                }}
                                              >
                                                {ans.mediaList.map((mItem, mIdx) => {
                                                  if (!mItem?.url) return null

                                                  if (mItem.type === 'image') {
                                                    return (
                                                      <Box
                                                        key={mIdx}
                                                        sx={{
                                                          display: 'flex',
                                                          flexDirection: 'column'
                                                        }}
                                                      >
                                                        <CardMedia
                                                          component="img"
                                                          src={mItem.url}
                                                          alt="Answer Media"
                                                          sx={{
                                                            maxWidth: 220,
                                                            borderRadius: 2,
                                                            boxShadow:
                                                              '0 10px 30px rgba(15,23,42,0.18)'
                                                          }}
                                                        />
                                                        <Button
                                                          sx={{ mt: 0.5, alignSelf: 'flex-start' }}
                                                          onClick={() => {
                                                            const formName = sanitizeFileName(
                                                              selectedForm?.name || 'Form'
                                                            )
                                                            const userName = sanitizeFileName(
                                                              userObj.userEmail
                                                            )
                                                            const questionNumber = ansIndex + 1

                                                            const extension = getFileExtension(
                                                              mItem.url,
                                                              'image'
                                                            )
                                                            const fileName = `${formName}-${userName}-${questionNumber}.${extension}`

                                                            downloadWithCustomName(
                                                              mItem.url,
                                                              fileName
                                                            )
                                                          }}
                                                        >
                                                          {t('cardReporting.downloadSingle')}
                                                        </Button>
                                                      </Box>
                                                    )
                                                  }

                                                  if (mItem.type === 'video') {
                                                    return (
                                                      <Box key={mIdx}>
                                                        <video
                                                          width="220"
                                                          height="auto"
                                                          controls
                                                          style={{
                                                            borderRadius: 12,
                                                            boxShadow:
                                                              '0 10px 30px rgba(15,23,42,0.25)'
                                                          }}
                                                        >
                                                          <source
                                                            src={mItem.url}
                                                            type="video/mp4"
                                                          />
                                                          {t('cardReporting.unsupportedMedia')}
                                                        </video>
                                                        <Button
                                                          sx={{ mt: 0.5 }}
                                                          onClick={() => {
                                                            const formName = sanitizeFileName(
                                                              selectedForm?.name || 'Form'
                                                            )
                                                            const userName = sanitizeFileName(
                                                              userObj.userEmail
                                                            )
                                                            const extension = getFileExtension(
                                                              mItem.url,
                                                              'video'
                                                            )
                                                            const fileName = `${formName}-${userName}-${ansIndex + 1}.${extension}`

                                                            downloadWithCustomName(
                                                              mItem.url,
                                                              fileName
                                                            )
                                                          }}
                                                        >
                                                          {t('cardReporting.downloadSingle') ||
                                                            'İndir'}
                                                        </Button>
                                                      </Box>
                                                    )
                                                  }

                                                  return (
                                                    <Typography variant="body2" key={mIdx}>
                                                      {t('cardReporting.unsupportedMedia')}
                                                    </Typography>
                                                  )
                                                })}
                                              </Box>
                                            )}

                                            {/* Ayrı videoUrl alanı */}
                                            {ans.videoUrl && typeof ans.videoUrl === 'string' && (
                                              <Box sx={{ mt: 0.75 }}>
                                                <video
                                                  width="360"
                                                  height="auto"
                                                  controls
                                                  style={{
                                                    borderRadius: 12,
                                                    marginTop: 4,
                                                    boxShadow:
                                                      '0 10px 30px rgba(15,23,42,0.25)'
                                                  }}
                                                >
                                                  <source src={ans.videoUrl} type="video/mp4" />
                                                  Tarayıcınız video etiketini desteklemiyor.
                                                </video>
                                                {(() => {
                                                  const formName = sanitizeFileName(
                                                    selectedForm?.name || 'Form'
                                                  )
                                                  const userName = sanitizeFileName(
                                                    userObj.userEmail
                                                  )
                                                  const extension = getFileExtension(
                                                    ans.videoUrl,
                                                    'video'
                                                  )
                                                  const fileName = `${formName}-${userName}-${
                                                    ansIndex + 1
                                                  }.${extension}`

                                                  return (
                                                    <Button
                                                      component="a"
                                                      href={ans.videoUrl}
                                                      download={fileName}
                                                      sx={{ mt: 0.5 }}
                                                    >
                                                      {t('cardReporting.downloadSingle')}
                                                    </Button>
                                                  )
                                                })()}
                                              </Box>
                                            )}
                                          </Box>
                                        </Box>
                                      )
                                    })}
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                          )
                        })
                      )}
                    </Grid>
                  )}
                </Paper>
              </>
            )}
          </Box>
        )}

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  )
}
